// File: frontend/src/components/SEOHead.tsx
// Versione per React 19+ - Nessuna dipendenza esterna richiesta!
// React 19 supporta nativamente <title>, <meta>, <link> nel componente

import React from 'react';
import { publications } from '../data/publications';

interface SEOHeadProps {
    title: string;
    description: string;
    canonical?: string;
    type?: 'website' | 'article';
    image?: string;
    publishedDate?: string;
    modifiedDate?: string;
    tags?: string[];
    noindex?: boolean;  // Override manuale per pagine specifiche
}

// Leggi dalle variabili d'ambiente
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://ti-mindmap-hub.com';
const ENABLE_SEO = process.env.REACT_APP_ENABLE_SEO === 'true';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = 'TI Mindmap HUB';
const TWITTER_HANDLE = '@ti_mindmap_hub';

export const SEOHead: React.FC<SEOHeadProps> = ({
    title,
    description,
    canonical,
    type = 'website',
    image = DEFAULT_IMAGE,
    publishedDate,
    modifiedDate,
    tags = [],
    noindex = false,
}) => {
    const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
    const url = canonical 
        ? `${SITE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}` 
        : SITE_URL;
    
    // Se SEO è disabilitato (ambiente dev) O se la pagina è marcata noindex
    const shouldNoIndex = !ENABLE_SEO || noindex;
    
    // Truncate description to 160 chars for SEO
    const metaDescription = description.length > 160 
        ? description.substring(0, 157) + '...' 
        : description;

    // Generate keywords from tags
    const defaultKeywords = [
        'threat intelligence',
        'CTI',
        'MITRE ATT&CK',
        'IOC',
        'CVE',
        'STIX',
        'cybersecurity',
        'malware analysis',
        'AI',
        'GenAI'
    ];
    const keywords = Array.from(new Set([...tags, ...defaultKeywords])).join(', ');

    // Structured Data based on page type
    const structuredData = type === 'article' 
        ? {
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': title,
            'description': metaDescription,
            'image': image,
            'datePublished': publishedDate,
            'dateModified': modifiedDate || publishedDate,
            'author': {
                '@type': 'Organization',
                'name': SITE_NAME,
                'url': SITE_URL
            },
            'publisher': {
                '@type': 'Organization',
                'name': SITE_NAME,
                'logo': {
                    '@type': 'ImageObject',
                    'url': `${SITE_URL}/logo192.png`
                }
            },
            'mainEntityOfPage': {
                '@type': 'WebPage',
                '@id': url
            },
            'keywords': keywords
        }
        : {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            'name': SITE_NAME,
            'description': metaDescription,
            'url': SITE_URL,
            'applicationCategory': 'SecurityApplication',
            'operatingSystem': 'Web',
            'offers': {
                '@type': 'Offer',
                'price': '0',
                'priceCurrency': 'USD'
            },
            'author': {
                '@type': 'Organization',
                'name': SITE_NAME
            },
            'subjectOf': publications.map((p) => ({
                '@type': 'ScholarlyArticle',
                'headline': p.title,
                'url': p.url,
                'sameAs': `https://doi.org/${p.doi}`,
                'identifier': { '@type': 'PropertyValue', 'propertyID': 'DOI', 'value': p.doi },
                'datePublished': String(p.year),
                'keywords': p.keywords.join(', '),
                'author': p.authors.map((a) => ({
                    '@type': 'Person',
                    'name': a.name,
                    'affiliation': { '@type': 'Organization', 'name': a.affiliation }
                })),
                'isPartOf': {
                    '@type': 'Periodical',
                    'name': p.venue,
                    'publisher': { '@type': 'Organization', 'name': 'IEEE' }
                }
            }))
        };

    // React 19: questi elementi vengono automaticamente hoistati nel <head>
    return (
        <>
            {/* Titolo */}
            <title>{fullTitle}</title>
            
            {/* Meta tags base */}
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={keywords} />
            <link rel="canonical" href={url} />
            
            {/* Robots - CRITICO per dev vs prod */}
            <meta 
                name="robots" 
                content={shouldNoIndex ? 'noindex, nofollow' : 'index, follow'} 
            />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={TWITTER_HANDLE} />
            <meta name="twitter:creator" content={TWITTER_HANDLE} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={image} />

            {/* Article specific meta tags */}
            {type === 'article' && publishedDate && (
                <meta property="article:published_time" content={publishedDate} />
            )}
            {type === 'article' && modifiedDate && (
                <meta property="article:modified_time" content={modifiedDate} />
            )}
            {type === 'article' && tags.map((tag, index) => (
                <meta property="article:tag" content={tag} key={`tag-${index}`} />
            ))}

            {/* Structured Data / JSON-LD - solo se SEO è abilitato */}
            {ENABLE_SEO && (
                <script 
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
                />
            )}
        </>
    );
};

export default SEOHead;
